import invariant from 'invariant';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useDataSourceLocation } from './DataSourceContext';
import getIndexOffset from './getIndexOffset';
export const AutocompleteContext = /*#__PURE__*/createContext({
  queryString: null,
  activeValue: null,
  selectionIndex: -1,
  dataSourceDescriptors: [],
  activationChecks: {
    current: {}
  },
  registerDataSource(type) {
    throw new Error(`registerDataSource called by "${type}" data source outside of context`);
  },
  deregisterDataSource() {
    throw new Error('deregisterDataSource called outside of context');
  },
  registerSelectCallback() {
    throw new Error(`registerSelectCallback called outside of context`);
  },
  deregisterSelectCallback() {
    throw new Error('deregisterSelectCallback called outside of context');
  },
  setActiveValue() {
    throw new Error('setActiveValue called outside of context');
  },
  selectValue() {
    throw new Error('selectValue called outside of context');
  },
  selectIndex() {
    throw new Error('selectIndex called outside of context');
  }
});
export const useAutocompleteQuery = () => {
  const {
    queryString
  } = useContext(AutocompleteContext);
  return queryString;
};
export const useLazyAutocompleteQuery = () => {
  const query = useAutocompleteQuery();
  const [debouncedValue, setDebouncedValue] = useState(query);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(query);
    }, 500);
    return () => clearTimeout(timeout);
  }, [query]);
  return debouncedValue;
};
export const useRegisterDataSource = (type, numberOfItems) => {
  const [isRegistered, setIsRegistered] = useState(false);
  const {
    registerDataSource,
    deregisterDataSource
  } = useContext(AutocompleteContext);
  const {
    index
  } = useDataSourceLocation();
  useEffect(() => {
    registerDataSource(type, index, numberOfItems);
    setIsRegistered(true);
    return () => {
      deregisterDataSource(type);
      setIsRegistered(false);
    };
  }, [type, numberOfItems, registerDataSource, deregisterDataSource, index]);
  return isRegistered;
};
export const useDataSourceDescriptors = () => {
  const {
    dataSourceDescriptors
  } = useContext(AutocompleteContext);
  return dataSourceDescriptors;
};
export const useActiveIndex = (type, canBeActivated) => {
  const {
    selectionIndex,
    selectIndex,
    dataSourceDescriptors,
    activationChecks
  } = useContext(AutocompleteContext);
  activationChecks.current[type] = canBeActivated;
  const dataSourceDescriptor = dataSourceDescriptors.find(dataSourceDescriptor => dataSourceDescriptor.type === type);
  const selectLocalIndex = useCallback(localIndex => selectIndex(type, localIndex), [selectIndex, type]);
  if (!dataSourceDescriptor) {
    return [-1, selectLocalIndex];
  }
  const offset = getIndexOffset(dataSourceDescriptors, dataSourceDescriptor);
  if (selectionIndex >= offset && selectionIndex < offset + dataSourceDescriptor.numberOfItems) {
    return [selectionIndex - offset, selectLocalIndex];
  }
  return [-1, selectLocalIndex];
};
export function useActivateValue() {
  const {
    setActiveValue,
    activeValue
  } = useContext(AutocompleteContext);
  const activateValue = useCallback((type, item) => {
    if (activeValue) {
      const [activeType, activeItem] = activeValue;
      if (activeType === type && activeItem.id === item.id) {
        return;
      }
    }
    setActiveValue([type, item]);
  }, [setActiveValue, activeValue]);
  const deactivateValue = useCallback(() => {
    setActiveValue(null);
  }, [setActiveValue]);
  return [activateValue, deactivateValue];
}
export function useSelectValue(onSelect) {
  const {
    selectValue,
    registerSelectCallback,
    deregisterSelectCallback
  } = useContext(AutocompleteContext);
  useEffect(() => {
    if (onSelect) {
      registerSelectCallback(onSelect);
      return () => {
        deregisterSelectCallback(onSelect);
      };
    }
    return;
  }, [deregisterSelectCallback, onSelect, registerSelectCallback]);
  return selectValue;
}
function mapIndex(dataSourceDescriptors, index) {
  let offset = 0;
  const dataSourceDescriptor = dataSourceDescriptors.find(({
    numberOfItems
  }) => {
    if (index < offset + numberOfItems) {
      return true;
    }
    offset = offset + numberOfItems;
    return false;
  });
  invariant(dataSourceDescriptor, `Could not map selection index "${index}" to a data source.`);
  return [dataSourceDescriptor, index - offset];
}
export function useSelectionIndex(dataSourceDescriptors, activationChecks) {
  const moveSelection = useCallback((currentIndex, increment) => {
    const resultSize = getResultSize(dataSourceDescriptors);
    let indexFound = false;
    let iteration = 0;

    // we're moving the selected index by `increment`.
    // however, since certain entries could be disabled
    // we need to check that we're not accidentally selecting
    // one of them.
    //
    // since we could now also run into the situation that
    // all items are disabled we need to keep track of the
    // number of times we tried to move the selection. because
    // if we've tried as many times as there are results then
    // we can stop because there is no new index we could select.
    while (!indexFound && iteration < resultSize) {
      const possibleIndex = (currentIndex + resultSize + increment + increment * iteration) % resultSize;
      iteration++;
      const [dataSourceDescriptor, localIndex] = mapIndex(dataSourceDescriptors, possibleIndex);
      const canBeActivated = activationChecks.current[dataSourceDescriptor.type];
      if (canBeActivated(localIndex)) {
        indexFound = true;
        return possibleIndex;
      }
    }
    return 0;
  }, [activationChecks, dataSourceDescriptors]);
  const [selectionIndex, setSelectionIndex] = useState(-1);
  const selectPrevious = useCallback(() => {
    setSelectionIndex(moveSelection(selectionIndex, -1));
  }, [moveSelection, selectionIndex]);
  const selectNext = useCallback(() => {
    setSelectionIndex(moveSelection(selectionIndex, 1));
  }, [moveSelection, selectionIndex]);
  const selectIndex = useCallback((type, localIndex) => {
    const dataSourceDescriptor = getDataSourceDescriptor(dataSourceDescriptors, type);
    const offset = getIndexOffset(dataSourceDescriptors, dataSourceDescriptor);
    const globalIndex = offset + localIndex;
    setSelectionIndex(globalIndex);
  }, [dataSourceDescriptors]);
  const resetSelection = useCallback(() => {
    // we're setting the "currentIndex" to -1 because
    // we're anticipating to increase it by 1 so that
    // we'd end up at 0.
    //
    // however, since the item at index 0 could be
    // disabled we cannot simply pick 0.
    // `moveSelection` takes care for these kinds of
    // edge cases for us and thus this essentially
    // means "pick the first index you can find with
    // an item that is not disabled."
    setSelectionIndex(moveSelection(-1, 1));
  }, [moveSelection]);
  return [selectionIndex, selectPrevious, selectNext, selectIndex, resetSelection];
}
function getDataSourceDescriptor(dataSourceDescriptors, type) {
  const dataSourceDescriptor = dataSourceDescriptors.find(dataSourceDescriptor => dataSourceDescriptor.type === type);
  invariant(dataSourceDescriptor, `Could not find descriptor for type "${type}".`);
  return dataSourceDescriptor;
}
const getResultSize = dataSourceDescriptors => dataSourceDescriptors.reduce((result, {
  numberOfItems
}) => result + numberOfItems, 0);